<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { Rocket, CreditCard, Sparkles, Wrench, Lock, FileText, Cookie } from 'lucide-vue-next';
import AppFooter from '@/components/AppFooter.vue';
import { i18n, t } from '@/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/help'));
const helpPath = (path: string) => localizedPath(siteLocale.value, path);

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: t('landingPages.help.index.seo.title'),
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { name: 'description', content: t('landingPages.help.index.seo.description') },
    { name: 'keywords', content: t('landingPages.help.index.seo.keywords') },
    { property: 'og:title', content: t('landingPages.help.index.seo.title') },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { property: 'og:description', content: t('landingPages.help.index.seo.description') },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: t('landingPages.help.index.seo.title') },
    { name: 'twitter:description', content: t('landingPages.help.index.seo.description') },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [{ rel: 'canonical', href: canonicalUrl.value }, ...buildPathHreflangLinks('/help')],
}));

const categoryDefs = [
  {
    key: 'getting_started',
    slug: 'getting-started',
    icon: Rocket,
  },
  {
    key: 'account_billing',
    slug: 'account-billing',
    icon: CreditCard,
    externalLink: '/pricing',
  },
  {
    key: 'features_tools',
    slug: 'features-tools',
    icon: Sparkles,
    externalLink: '/ai-design-tools',
  },
  {
    key: 'troubleshooting',
    slug: 'troubleshooting',
    icon: Wrench,
  },
] as const;

const categories = computed(() =>
  categoryDefs.map((def) => ({
    ...def,
    title: t(`landingPages.help.index.category.${def.key}.title`),
    description: t(`landingPages.help.index.category.${def.key}.description`),
  }))
);

type FaqItem = {
  question: string;
  answer?: string;
  answerParagraph1?: string;
  answerParagraph2?: string;
  answerParagraph3?: string;
  listItems?: string[];
  listItems2?: string[];
};

const faqItems = computed(() => {
  const raw = i18n.getRaw('landingPages.help.index.faq.items');
  return Array.isArray(raw) ? (raw as FaqItem[]) : [];
});

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <AppNav active-path="/help" />

    <!-- Hero Section -->
    <section class="relative pt-48 pb-32 overflow-hidden help-hero">
      <div class="relative container mx-auto px-6 md:px-4 text-center z-10">
        <h1
          class="font-boldonse text-5xl md:text-7xl text-white uppercase tracking-tight mb-8 drop-shadow-sm"
        >
          {{ t('landingPages.help.index.hero.title') }}
        </h1>
        <h2 class="text-white/90 text-xl max-w-2xl mx-auto font-medium">
          {{ t('landingPages.help.index.hero.subtitle') }}
        </h2>
      </div>
    </section>

    <!-- Categories Section -->
    <section class="py-24 bg-white min-h-[50vh]">
      <div class="container mx-auto px-6 md:px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <template v-for="category in categories" :key="category.key">
            <NuxtLink
              v-if="category.externalLink"
              :to="helpPath(category.externalLink)"
              class="p-8 rounded-3xl bg-[#fafafa] hover:bg-[#f0f0f0] transition-all duration-300 cursor-pointer group border border-transparent hover:border-black/5"
            >
              <div class="mb-6">
                <component :is="category.icon" class="w-10 h-10" />
              </div>
              <h3 class="text-2xl font-medium text-black/90 mb-3 group-hover:text-black">
                {{ category.title }}
              </h3>
              <p class="text-black/60 group-hover:text-black/80 leading-relaxed">
                {{ category.description }}
              </p>
            </NuxtLink>
            <NuxtLink
              v-else
              :to="helpPath(`/help/${category.slug}`)"
              class="p-8 rounded-3xl bg-[#fafafa] hover:bg-[#f0f0f0] transition-all duration-300 cursor-pointer group border border-transparent hover:border-black/5"
            >
              <div class="mb-6">
                <component :is="category.icon" class="w-10 h-10" />
              </div>
              <h3 class="text-2xl font-medium text-black/90 mb-3 group-hover:text-black">
                {{ category.title }}
              </h3>
              <p class="text-black/60 group-hover:text-black/80 leading-relaxed">
                {{ category.description }}
              </p>
            </NuxtLink>
          </template>
        </div>

        <!-- Contact Support -->
        <div class="mt-24 text-center">
          <h2 class="text-3xl font-medium text-black/90 mb-6">
            {{ t('landingPages.help.index.contact.heading') }}
          </h2>
          <a
            href="mailto:help@veeso.ai"
            class="inline-block px-8 py-4 bg-black text-white rounded-full font-bold hover:bg-black/80 transition-colors"
          >
            {{ t('landingPages.help.index.contact.cta') }}
          </a>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-24 bg-[#fafafa]">
      <div class="container mx-auto px-6 md:px-4">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-4xl font-medium text-black/90 mb-12 text-center">
            {{ t('landingPages.help.index.faq.title') }}
          </h2>
          <div class="space-y-4">
            <div
              v-for="(item, index) in faqItems"
              :key="index"
              class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
            >
              <button
                type="button"
                @click="toggleFaq(index)"
                class="flex items-center justify-between w-full text-left focus:outline-none"
              >
                <h3 class="text-xl font-medium text-black/90 pr-4">{{ item.question }}</h3>
                <span
                  class="transform transition-transform duration-300 text-2xl shrink-0"
                  :class="{ 'rotate-45': expandedFaqIndex === index }"
                >
                  +
                </span>
              </button>
              <div
                class="grid transition-all duration-300 ease-in-out overflow-hidden"
                :class="
                  expandedFaqIndex === index
                    ? 'grid-rows-[1fr] opacity-100 mt-4'
                    : 'grid-rows-[0fr] opacity-0'
                "
              >
                <div class="overflow-hidden">
                  <div v-if="item.answerParagraph1" class="space-y-3">
                    <p class="text-black/60 leading-relaxed text-lg">
                      {{ item.answerParagraph1 }}
                    </p>
                    <ul
                      v-if="item.listItems && item.listItems.length > 0"
                      class="space-y-2 pl-0 mt-3"
                    >
                      <li
                        v-for="(listItem, listIndex) in item.listItems"
                        :key="listIndex"
                        class="text-black/60 leading-relaxed text-lg flex items-start"
                      >
                        <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                        <span>{{ listItem }}</span>
                      </li>
                    </ul>
                    <p v-if="item.answerParagraph2" class="text-black/60 leading-relaxed text-lg">
                      {{ item.answerParagraph2 }}
                    </p>
                    <ul
                      v-if="item.listItems2 && item.listItems2.length > 0"
                      class="space-y-2 pl-0 mt-3"
                    >
                      <li
                        v-for="(listItem, listIndex) in item.listItems2"
                        :key="listIndex"
                        class="text-black/60 leading-relaxed text-lg flex items-start"
                      >
                        <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                        <span>{{ listItem }}</span>
                      </li>
                    </ul>
                    <p v-if="item.answerParagraph3" class="text-black/60 leading-relaxed text-lg">
                      {{ item.answerParagraph3 }}
                    </p>
                  </div>
                  <p v-else class="text-black/60 leading-relaxed text-lg">
                    {{ item.answer }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Legal Section -->
    <section class="py-24 bg-white border-t border-black/5">
      <div class="container mx-auto px-6 md:px-4">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-3xl font-medium text-black/90 mb-12 text-center">
            {{ t('landingPages.help.index.legal.heading') }}
          </h2>
          <div class="flex flex-col md:flex-row gap-6">
            <NuxtLink
              :to="helpPath('/privacy')"
              class="flex-1 p-8 rounded-3xl bg-[#fafafa] hover:bg-[#f0f0f0] transition-all group"
            >
              <div
                class="mb-4 flex justify-start text-black/90 group-hover:text-black transition-colors"
              >
                <Lock class="w-8 h-8" />
              </div>
              <h3 class="text-xl font-medium text-black/90 mb-2 group-hover:text-black">
                {{ t('landingPages.help.index.legal.privacy.title') }}
              </h3>
              <p class="text-sm text-black/50">
                {{ t('landingPages.help.index.legal.privacy.description') }}
              </p>
            </NuxtLink>
            <NuxtLink
              :to="helpPath('/terms')"
              class="flex-1 p-8 rounded-3xl bg-[#fafafa] hover:bg-[#f0f0f0] transition-all group"
            >
              <div
                class="mb-4 flex justify-start text-black/90 group-hover:text-black transition-colors"
              >
                <FileText class="w-8 h-8" />
              </div>
              <h3 class="text-xl font-medium text-black/90 mb-2 group-hover:text-black">
                {{ t('landingPages.help.index.legal.terms.title') }}
              </h3>
              <p class="text-sm text-black/50">
                {{ t('landingPages.help.index.legal.terms.description') }}
              </p>
            </NuxtLink>
            <NuxtLink
              :to="helpPath('/cookie-policy')"
              class="flex-1 p-8 rounded-3xl bg-[#fafafa] hover:bg-[#f0f0f0] transition-all group"
            >
              <div
                class="mb-4 flex justify-start text-black/90 group-hover:text-black transition-colors"
              >
                <Cookie class="w-8 h-8" />
              </div>
              <h3 class="text-xl font-medium text-black/90 mb-2 group-hover:text-black">
                {{ t('landingPages.help.index.legal.cookie.title') }}
              </h3>
              <p class="text-sm text-black/50">
                {{ t('landingPages.help.index.legal.cookie.description') }}
              </p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>
    <AppFooter />
  </div>
</template>

<style scoped>
.help-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.help-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}
</style>
